import { useState, useEffect } from 'react';
import { useData } from '../../../contexts/DataContext';
import api from '../../../services/api';

export const useRulesData = () => {
  const {
    applyRulesToTransactions,
    categories,
    rules,
    addRule,
    updateRule,
    deleteRule,
    getTransactionFields,
    isLoading,
    transactions,
    updateTransaction,
    fetchTransactions
  } = useData();

  // State for feedback and operations
  const [result, setResult] = useState(null);
  const [applyingRules, setApplyingRules] = useState(false);
  const [aiCategorizing, setAiCategorizing] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiRuleSuggestions, setAiRuleSuggestions] = useState([]);
  // Dismissible AI feedback: { severity: 'success'|'info'|'error', text }.
  // Success/info fade out on their own; errors stay until dismissed.
  const [aiMessage, setAiMessage] = useState(null);
  const [aiConsolidating, setAiConsolidating] = useState(false);
  const [consolidationProposals, setConsolidationProposals] = useState(null);
  const [availableFields, setAvailableFields] = useState([]);
  const [uncategorizedCount, setUncategorizedCount] = useState(0);
  const [categorizeSuccess, setCategorizeSuccess] = useState(false);
  const [categorizeMessage, setCategorizeMessage] = useState('');

  // Calculate uncategorized count
  useEffect(() => {
    const count = transactions.filter(t => 
      !t.category || t.category === 'uncategorized'
    ).length;
    setUncategorizedCount(count);
  }, [transactions]);

  // Set available fields
  useEffect(() => {
    setAvailableFields(getTransactionFields());
  }, [getTransactionFields]);

  // Auto-dismiss AI feedback so it doesn't sit on the page forever. Long
  // enough to read a couple of sentences; errors stay until dismissed.
  useEffect(() => {
    if (!aiMessage || aiMessage.severity === 'error') return undefined;
    const timer = setTimeout(() => setAiMessage(null), 12000);
    return () => clearTimeout(timer);
  }, [aiMessage]);

  // Auto-clear success feedback after 3 seconds
  useEffect(() => {
    if (categorizeSuccess) {
      const timer = setTimeout(() => {
        setCategorizeSuccess(false);
        setCategorizeMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [categorizeSuccess]);

  // Reset success if new uncategorized transactions appear
  useEffect(() => {
    if (uncategorizedCount > 0 && categorizeSuccess) {
      setCategorizeSuccess(false);
      setCategorizeMessage('');
    }
  }, [uncategorizedCount, categorizeSuccess]);

  // Sort rules by priority globally
  const sortedRules = [...rules].sort((a, b) => a.priority - b.priority);

  // Group rules by category
  const groupRulesByCategory = () => {
    const grouped = {};

    // Create a group for each category
    categories.forEach(cat => {
      grouped[cat.id] = {
        category: cat,
        rules: []
      };
    });

    // Add uncategorized group if needed
    if (!grouped.uncategorized) {
      grouped.uncategorized = {
        category: { id: 'uncategorized', name: 'Uncategorized', color: '#999' },
        rules: []
      };
    }

    // Add rules to their respective groups
    sortedRules.forEach(rule => {
      if (grouped[rule.categoryId]) {
        grouped[rule.categoryId].rules.push(rule);
      } else {
        grouped.uncategorized.rules.push(rule);
      }
    });

    // Convert to array and sort categories alphabetically
    return Object.values(grouped)
      .filter(group => group.rules.length > 0)
      .sort((a, b) => a.category.name.localeCompare(b.category.name));
  };

  // Rule application operations
  const handleRunAll = async () => {
    setApplyingRules(true);
    try {
      const count = await applyRulesToTransactions(sortedRules.map(r => r._id));
      setResult({ success: true, message: `Applied all rules to ${count} transactions` });
    } catch (error) {
      setResult({ success: false, message: error.message || 'Failed to apply all rules' });
    } finally { 
      setApplyingRules(false); 
    }
  };

  const handleRunSelected = async (selectedRuleIds) => {
    if (!selectedRuleIds.length) return;

    // Sort selected rules by priority before applying
    const selectedRules = sortedRules.filter(rule => selectedRuleIds.includes(rule._id));
    const sortedSelectedIds = selectedRules.map(rule => rule._id);

    setApplyingRules(true);
    try {
      const count = await applyRulesToTransactions(sortedSelectedIds);
      setResult({ 
        success: true, 
        message: `Applied ${sortedSelectedIds.length} rule(s) to ${count} transactions` 
      });
    } catch (error) {
      setResult({ success: false, message: error.message || 'Failed to apply selected rules' });
    } finally { 
      setApplyingRules(false); 
    }
  };

  const handleRunSingle = async (id) => {
    setApplyingRules(true);
    try {
      const count = await applyRulesToTransactions([id]);
      setResult({ success: true, message: `Rule applied to ${count} transactions` });
    } catch (error) {
      setResult({ success: false, message: error.message || 'Failed to apply rule' });
    } finally { 
      setApplyingRules(false); 
    }
  };

  const handleRunCategoryRules = async (categoryId) => {
    const categoryRules = rules.filter(rule => rule.categoryId === categoryId);
    if (!categoryRules.length) return;

    // Sort by priority before applying
    const sortedCategoryRules = [...categoryRules].sort((a, b) => a.priority - b.priority);
    const categoryRuleIds = sortedCategoryRules.map(rule => rule._id);

    setApplyingRules(true);
    try {
      const count = await applyRulesToTransactions(categoryRuleIds);
      const categoryName = categories.find(c => c.id === categoryId)?.name || 'Unknown';
      setResult({
        success: true,
        message: `Applied ${categoryRuleIds.length} rule(s) for ${categoryName} to ${count} transactions`
      });
    } catch (error) {
      const categoryName = categories.find(c => c.id === categoryId)?.name || 'Unknown';
      setResult({ success: false, message: error.message || `Failed to apply rules for ${categoryName}` });
    } finally { 
      setApplyingRules(false); 
    }
  };

  const handleCategorizeAll = async () => {
    if (!uncategorizedCount) return;
    setApplyingRules(true);
    setCategorizeSuccess(false);
    setCategorizeMessage('');
    
    try {
      // Apply all rules to uncategorized transactions only
      const count = await applyRulesToTransactions(
        sortedRules.map(r => r._id),
        { onlyUncategorized: true }
      );
      setResult({
        success: true,
        message: `Categorized ${count} of ${uncategorizedCount} transactions`
      });
      setCategorizeSuccess(true);
      setCategorizeMessage(`Categorized ${count} of ${uncategorizedCount} transactions!`);
      
      // Update uncategorized count after rules are applied
      const updatedCount = transactions.filter(t =>
        !t.category || t.category === 'uncategorized'
      ).length;
      setUncategorizedCount(updatedCount);
    } catch (error) {
      setResult({ success: false, message: error.message || 'Failed to categorize transactions' });
      setCategorizeSuccess(false);
      setCategorizeMessage('Failed to categorize transactions');
    } finally {
      setApplyingRules(false);
    }
  };

  // transactionIds: optional array to scope the run to selected transactions.
  // (When used directly as an onClick handler the first arg is the click
  // event, so only treat a real array as a scope.)
  const handleAiCategorize = async (transactionIds) => {
    const ids = Array.isArray(transactionIds) ? transactionIds : null;
    if (!uncategorizedCount) return;
    setAiCategorizing(true);
    setAiMessage(null);

    try {
      const { data } = await api.post('/ai/categorize', ids ? { transactionIds: ids } : {});
      await fetchTransactions({ silent: true });
      setAiSuggestions(data.suggestions || []);
      setAiRuleSuggestions(data.ruleSuggestions || []);
      setUncategorizedCount(data.remaining);

      if (data.processed === 0) {
        setAiMessage({
          severity: 'info',
          text: ids
            ? 'The selected transactions are already categorized.'
            : 'No uncategorized transactions to process.'
        });
      } else if (data.applied === 0 && !(data.suggestions?.length)) {
        setAiMessage({
          severity: 'info',
          text: data.processed === 1
            ? "Couldn't find a confident match for that transaction, so it's untouched. Categorize it yourself and the next run will learn from it."
            : `Couldn't find a confident match for those ${data.processed} transactions, so they're untouched. Categorize one yourself and the next run will learn from it.`
        });
      } else {
        const sentences = [];
        const suggested = data.suggestions?.length || 0;
        if (data.applied === data.processed) {
          sentences.push(
            data.applied === 1
              ? 'Done! Your transaction is categorized.'
              : `Done! All ${data.applied} transactions are categorized.`
          );
        } else if (data.applied > 0) {
          sentences.push(`Categorized ${data.applied} of ${data.processed} transactions.`);
        }
        if (suggested) {
          sentences.push(
            data.applied > 0
              ? (suggested === 1
                  ? 'One more is waiting below. Tap the tick if it looks right.'
                  : `${suggested} more are waiting below. Tap the tick on the ones that look right.`)
              : (suggested === 1
                  ? 'Found a likely match for one. It’s waiting below; tap the tick if it looks right.'
                  : `Found likely matches for ${suggested} of them. They’re waiting below; tap the tick on the ones that look right.`)
          );
        }
        if (data.ruleSuggestions?.length) {
          sentences.push(
            data.ruleSuggestions.length === 1
              ? 'There’s also a suggested rule so these get sorted automatically next time.'
              : `There are also ${data.ruleSuggestions.length} suggested rules so these get sorted automatically next time.`
          );
        }
        setAiMessage({ severity: 'success', text: sentences.join(' ') });
      }
    } catch (error) {
      setAiMessage({
        severity: 'error',
        text: error.response?.data?.error || 'AI categorization failed'
      });
    } finally {
      setAiCategorizing(false);
    }
  };

  const handleDismissAiMessage = () => setAiMessage(null);

  // selectedRuleIds: optional array of rule ids to scope consolidation to.
  // Empty/missing selection means consolidate across all rules.
  const handleConsolidateRules = async (selectedRuleIds) => {
    const ids = Array.isArray(selectedRuleIds) ? selectedRuleIds : [];
    if (ids.length === 1) {
      setAiMessage({
        severity: 'info',
        text: 'Select at least two rules to consolidate them, or clear the selection to consolidate all rules.'
      });
      return;
    }
    setAiConsolidating(true);
    setAiMessage(null);
    try {
      const { data } = await api.post('/ai/consolidate-rules', ids.length ? { ruleIds: ids } : {});
      if (!data.proposals.length) {
        setAiMessage({
          severity: 'info',
          text: ids.length
            ? `Reviewed the ${data.reviewed} selected rules. None of them can be merged without changing what they match.`
            : `Reviewed all ${data.reviewed} rules. None of them can be merged without changing what they match.`
        });
      } else {
        setConsolidationProposals(data.proposals);
      }
    } catch (error) {
      setAiMessage({
        severity: 'error',
        text: error.response?.data?.error || 'Rule consolidation failed'
      });
    } finally {
      setAiConsolidating(false);
    }
  };

  const handleApplyConsolidation = async (proposal) => {
    try {
      const maxPriority = rules.reduce((max, r) => Math.max(max, r.priority || 0), 0);
      await addRule({
        name: proposal.name,
        conditionGroups: proposal.conditionGroups,
        categoryId: proposal.categoryId,
        priority: maxPriority + 1
      });
      for (const ruleId of proposal.ruleIds) {
        await deleteRule(ruleId);
      }
      setConsolidationProposals(prev =>
        (prev || []).filter(p => p !== proposal)
      );
      setResult({
        success: true,
        message: `Merged ${proposal.ruleIds.length} rules into "${proposal.name}"`
      });
      return { success: true };
    } catch {
      setResult({ success: false, message: 'Failed to apply consolidation' });
      return { success: false };
    }
  };

  const handleCloseConsolidation = () => setConsolidationProposals(null);

  const handleAcceptAiSuggestion = async (suggestion) => {
    const txn = transactions.find(
      t => (t._id || t.id) === suggestion.id || t._id === suggestion.id
    );
    if (!txn) {
      setAiSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
      return;
    }
    try {
      await updateTransaction({ ...txn, category: suggestion.categoryId });
      setAiSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
      setUncategorizedCount(prev => Math.max(0, prev - 1));
      setResult({
        success: true,
        message: `Categorized as ${suggestion.categoryName}`
      });
    } catch {
      setResult({ success: false, message: 'Failed to apply suggestion' });
    }
  };

  const handleRejectAiSuggestion = (suggestionId) => {
    setAiSuggestions(prev => prev.filter(s => s.id !== suggestionId));
  };

  const handleCreateAiRule = async (ruleSuggestion) => {
    try {
      const maxPriority = rules.reduce(
        (max, r) => Math.max(max, r.priority || 0),
        0
      );
      await addRule({
        name: ruleSuggestion.name,
        conditionGroups: [{
          conditions: [{
            field: 'description',
            operator: 'contains',
            value: ruleSuggestion.keyword
          }]
        }],
        categoryId: ruleSuggestion.categoryId,
        priority: maxPriority + 1
      });
      setAiRuleSuggestions(prev =>
        prev.filter(r => r.keyword !== ruleSuggestion.keyword || r.categoryId !== ruleSuggestion.categoryId)
      );
      setResult({ success: true, message: `Rule created: ${ruleSuggestion.name}` });
    } catch {
      setResult({ success: false, message: 'Failed to create rule' });
    }
  };

  const handleDismissAiRule = (ruleSuggestion) => {
    setAiRuleSuggestions(prev =>
      prev.filter(r => r.keyword !== ruleSuggestion.keyword || r.categoryId !== ruleSuggestion.categoryId)
    );
  };

  // Rule CRUD operations
  const handleAddRule = async (ruleData) => {
    try {
      await addRule(ruleData);
      setResult({ success: true, message: 'Rule created' });
      return { success: true };
    } catch (error) {
      setResult({ success: false, message: 'Failed to save rule' });
      return { success: false, error };
    }
  };

  const handleUpdateRule = async (ruleId, ruleData) => {
    try {
      await updateRule(ruleId, ruleData);
      setResult({ success: true, message: 'Rule updated' });
      return { success: true };
    } catch (error) {
      setResult({ success: false, message: 'Failed to save rule' });
      return { success: false, error };
    }
  };

  const handleDeleteRule = async (id) => {
    try { 
      await deleteRule(id); 
      setResult({ success: true, message: 'Rule deleted' }); 
    } catch { 
      setResult({ success: false, message: 'Failed to delete rule' }); 
    }
  };

  // Transaction operations
  const handleBulkCategorize = async (transactionIds, categoryId) => {
    try {
      // Find the transactions to update
      const transactionsToUpdate = transactions.filter(t =>
        transactionIds.includes(t._id || t.id)
      );

      // Update each transaction with the new category
      for (const transaction of transactionsToUpdate) {
        const updatedTransaction = { ...transaction, category: categoryId };
        await updateTransaction(updatedTransaction);
      }

      // Show success message
      const categoryName = categories.find(c => c.id === categoryId)?.name || 'Unknown';
      setResult({
        success: true,
        message: `Assigned ${transactionIds.length} transaction(s) to ${categoryName}`
      });

      // Update uncategorized count if needed
      if (categoryId !== 'uncategorized') {
        const updatedCount = transactions.filter(t =>
          !t.category || t.category === 'uncategorized'
        ).length - transactionIds.length;
        setUncategorizedCount(Math.max(0, updatedCount));
      }
    } catch (error) {
      console.error('Error updating transactions:', error);
      setResult({
        success: false,
        message: 'Failed to assign category to transactions'
      });
    }
  };

  // Helper functions
  const getCategoryName = (id) => categories.find(c => c.id === id)?.name || 'Unknown';

  const formatCondition = (c) => {
    let val = c.value;
    if (c.operator === 'between' && c.value.includes(',')) {
      const [a, b] = c.value.split(','); 
      val = `${a} and ${b}`;
    }
    const label = availableFields.find(f => f.name === c.field)?.label || c.field;
    const op = { 
      contains: 'contains', 
      equals: 'equals', 
      greaterThan: 'is greater than', 
      lessThan: 'is less than', 
      startsWith: 'starts with', 
      endsWith: 'ends with', 
      between: 'is between' 
    }[c.operator] || c.operator;
    return `${label} ${op} "${val}"`;
  };

  return {
    // Data
    rules: sortedRules,
    groupedRules: groupRulesByCategory(),
    categories,
    transactions,
    availableFields,
    isLoading,
    
    // State
    result,
    applyingRules,
    aiCategorizing,
    aiSuggestions,
    aiRuleSuggestions,
    aiMessage,
    aiConsolidating,
    consolidationProposals,
    uncategorizedCount,
    categorizeSuccess,
    categorizeMessage,

    // Operations
    handleRunAll,
    handleRunSelected,
    handleRunSingle,
    handleRunCategoryRules,
    handleCategorizeAll,
    handleAiCategorize,
    handleAcceptAiSuggestion,
    handleRejectAiSuggestion,
    handleCreateAiRule,
    handleDismissAiRule,
    handleDismissAiMessage,
    handleConsolidateRules,
    handleApplyConsolidation,
    handleCloseConsolidation,
    handleAddRule,
    handleUpdateRule,
    handleDeleteRule,
    handleBulkCategorize,
    
    // Helpers
    getCategoryName,
    formatCondition,
    
    // State setters (for components that need direct access)
    setResult
  };
};
